/**
 * Cycles example prompts through a text field's placeholder.
 *
 * These used to be tappable chips in a row of their own. As chips they cost a
 * permanent block of vertical space on the one screen with the least to spare,
 * to show four examples of a thing whose whole point is that you can say
 * anything. As a rotating placeholder they teach the same thing — here is the
 * kind of sentence that works — in space the input already occupies.
 *
 * Freezes on focus. Text sliding around underneath a cursor someone has just
 * placed there is the sort of motion that reads as a glitch, and by then the
 * hint has done its job anyway.
 *
 * Reduced motion gets the first hint and nothing else: the rotation is
 * decorative, and a caption changing on a timer is exactly what that
 * preference is asking us not to do.
 */
import { useEffect, useState } from 'react';
import useReducedMotion from './useReducedMotion';

export const HINT_INTERVAL_MS = 3600;

export default function useRotatingHint(hints, { paused = false } = {}) {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const count = hints?.length || 0;

  useEffect(() => {
    if (reduced || paused || count < 2) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), HINT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [reduced, paused, count]);

  // Guards a hint list that shrinks between renders — index would otherwise
  // point past the end until the next tick and render nothing.
  return count ? hints[index % count] : '';
}
